import React, { useState } from 'react';
import { Plus, MessageSquare, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDateFnsLocale } from '../../hooks/useDateFnsLocale';
import { useAiChatStore } from '../../stores/aiChatStore';
import { format } from 'date-fns';

export interface AiChatSidebarProps {
  onClose?: () => void;
}

export const AiChatSidebar: React.FC<AiChatSidebarProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();
  const { conversations, currentConversationId, createConversation, selectConversation, deleteConversation } = useAiChatStore();
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleNewConversation = async () => {
    setIsCreating(true);
    await createConversation();
    setIsCreating(false);
    setMenuOpenId(null);
    onClose?.();
  };

  const handleSelect = async (id: string) => {
    await selectConversation(id);
    setMenuOpenId(null);
    onClose?.();
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteConversation(id);
    setMenuOpenId(null);
  };

  const toggleMenu = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpenId((prev) => (prev === id ? null : id));
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return t('aiChat.sidebar.today');
      if (diffDays === 1) return t('aiChat.sidebar.yesterday');
      if (diffDays < 7) return format(date, 'EEEE', { locale: dateFnsLocale });
      return format(date, 'MMM d', { locale: dateFnsLocale });
    } catch {
      return '';
    }
  };

  return (
    <div className="flex flex-col h-full pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={handleNewConversation}
          disabled={isCreating}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          {t('aiChat.sidebar.newConversation')}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <MessageSquare className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('aiChat.sidebar.empty.title')}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('aiChat.sidebar.empty.subtitle')}</p>
          </div>
        ) : (
          <div className="py-1">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => handleSelect(conv.id)}
                className={`
                  group relative flex items-center gap-2 px-3 py-2.5 mx-2 rounded-lg cursor-pointer transition-colors
                  ${conv.id === currentConversationId
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}
                `}
              >
                <MessageSquare className="w-4 h-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{conv.title || t('aiChat.sidebar.defaultTitle')}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">{formatDate(conv.updatedAt)}</p>
                </div>
                <div className="relative">
                  <button
                    onClick={(e) => toggleMenu(conv.id, e)}
                    className="p-1 min-h-[44px] min-w-[44px] flex items-center justify-center rounded opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                    aria-label={t('aiChat.sidebar.options')}
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </button>
                  {menuOpenId === conv.id && (
                    <div className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        {t('aiChat.sidebar.rename')}
                      </button>
                      <button
                        onClick={(e) => handleDelete(conv.id, e)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t('aiChat.sidebar.delete')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiChatSidebar;
